/**
 * Vanity Configurator — Shopify Product Page Embed Script
 *
 * Installation:
 *   Add this script to your Shopify theme's product page template
 *   (e.g. theme.liquid or product.liquid), or install it via the
 *   Shopify Script Editor / Theme Code Editor as a custom script tag.
 *
 * Metafield requirement:
 *   The product must have a metafield with the following coordinates:
 *     namespace: "custom"
 *     key:       "visualizerv2url"
 *
 *   The metafield value should be a URL pointing to your visualizer app,
 *   e.g.:  https://your-configurator-app.web.app/?productId=123
 *
 *   Full metafield namespace+key: custom.visualizerv2url
 *
 * Liquid setup (required — add once to your product.liquid template):
 *   <div
 *     id="vanity-configurator-data"
 *     data-visualizer-url="{{ product.metafields.custom.visualizerv2url | escape }}"
 *     style="display:none">
 *   </div>
 *
 * ─── How it works ─────────────────────────────────────────────────────────────
 *   1. Reads the visualizerUrl from the data attribute rendered by Liquid.
 *   2. If a URL is present, hides `.grid.product-single` via CSS injection.
 *   3. Injects a full-width iframe container above (or replacing) the hidden block.
 *   4. The iframe src is set to the metafield URL so the configurator loads
 *      pre-configured for this product.
 */

(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────

  /** CSS selector for the native Shopify product section to hide. */
  var PRODUCT_GRID_SELECTOR = '.grid.product-single';

  /** ID of the hidden data element injected by Liquid. */
  var DATA_ELEMENT_ID = 'vanity-configurator-data';

  /** ID given to the injected iframe wrapper (for deduplication). */
  var EMBED_WRAPPER_ID = 'vanity-configurator-embed';

  /**
   * Height of the embedded configurator.
   * Use a CSS value: '100vh', '800px', 'calc(100vh - 80px)', etc.
   */
  var IFRAME_HEIGHT = '100vh';

  // ── Utilities ──────────────────────────────────────────────────────────────

  function log(msg) {
    console.log('[Vanity Configurator]', msg);
  }

  function warn(msg) {
    console.warn('[Vanity Configurator]', msg);
  }

  // ── Core Logic ─────────────────────────────────────────────────────────────

  /**
   * Reads the visualizerUrl from the Liquid-rendered data element.
   * Returns null if the element or attribute is missing / empty.
   */
  function getVisualizerUrl() {
    var el = document.getElementById(DATA_ELEMENT_ID);
    if (!el) {
      warn(
        'Data element #' +
          DATA_ELEMENT_ID +
          ' not found. ' +
          'Make sure you added the Liquid snippet to your product template.'
      );
      return null;
    }
    var url = (el.getAttribute('data-visualizer-url') || '').trim();
    return url.length > 0 ? url : null;
  }

  /**
   * Hides the native product grid section via an injected <style> tag.
   * Using a <style> tag (rather than direct style manipulation) ensures the
   * hide survives any theme JS that manipulates DOM classes later.
   */
  function hideProductGrid() {
    if (document.getElementById('vanity-configurator-hide-style')) return;

    var style = document.createElement('style');
    style.id = 'vanity-configurator-hide-style';
    style.textContent =
      PRODUCT_GRID_SELECTOR +
      ' { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(style);

    log('Native product grid hidden (' + PRODUCT_GRID_SELECTOR + ').');
  }

  /**
   * Builds and injects the iframe embed wrapper into the DOM.
   * The wrapper is inserted immediately before the product grid element
   * so it occupies the same visual position in the page flow.
   */
  function injectIframe(visualizerUrl) {
    // Prevent duplicate injection on re-runs / theme re-hydration
    if (document.getElementById(EMBED_WRAPPER_ID)) {
      log('Embed already present — skipping injection.');
      return;
    }

    // Find the anchor element to insert before
    var productGrid = document.querySelector(PRODUCT_GRID_SELECTOR);

    // ── Wrapper ──────────────────────────────────────────────────────────────
    var wrapper = document.createElement('div');
    wrapper.id = EMBED_WRAPPER_ID;
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', 'Vanity Configurator');
    Object.assign(wrapper.style, {
      position: 'relative',
      width: '100%',
      height: IFRAME_HEIGHT,
      maxWidth: '100%',
      margin: '0',
      padding: '0',
      background: '#ECF2F3', // Brand background from blueprint
      border: 'none',
      overflow: 'hidden',
    });

    // ── Loading indicator (shown until iframe fires 'load') ──────────────────
    var loader = document.createElement('div');
    loader.id = 'vanity-configurator-loader';
    Object.assign(loader.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#ECF2F3',
      zIndex: '10',
      gap: '16px',
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#4a6572',
    });
    loader.innerHTML =
      '<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style="animation: vanity-spin 1s linear infinite;">' +
      '<circle cx="24" cy="24" r="20" stroke="#66B2CC" stroke-width="4" stroke-dasharray="100 28" stroke-linecap="round"/>' +
      '</svg>' +
      '<span style="font-size:14px;font-weight:500;letter-spacing:0.02em;">Loading Vanity Configurator…</span>';

    // Inject spinner keyframes once
    if (!document.getElementById('vanity-configurator-spinner-style')) {
      var spinStyle = document.createElement('style');
      spinStyle.id = 'vanity-configurator-spinner-style';
      spinStyle.textContent =
        '@keyframes vanity-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
      document.head.appendChild(spinStyle);
    }

    // ── iframe ───────────────────────────────────────────────────────────────
    var iframe = document.createElement('iframe');
    iframe.src = visualizerUrl;
    iframe.title = 'Vanity Configurator';
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('allow', 'fullscreen');
    iframe.setAttribute('loading', 'eager');
    Object.assign(iframe.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      border: 'none',
      outline: 'none',
      opacity: '0',
      transition: 'opacity 0.4s ease',
    });

    iframe.addEventListener('load', function () {
      iframe.style.opacity = '1';
      if (loader.parentNode) {
        loader.style.transition = 'opacity 0.3s ease';
        loader.style.opacity = '0';
        setTimeout(function () {
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        }, 300);
      }
      log('Configurator iframe loaded from: ' + visualizerUrl);
    });

    // ── Assemble & insert ────────────────────────────────────────────────────
    wrapper.appendChild(loader);
    wrapper.appendChild(iframe);

    if (productGrid && productGrid.parentNode) {
      productGrid.parentNode.insertBefore(wrapper, productGrid);
      log('Embed injected before product grid.');
    } else {
      // Fallback: append to main content area if product grid not found
      var mainContent =
        document.querySelector('main') ||
        document.querySelector('#MainContent') ||
        document.querySelector('.page-container') ||
        document.body;
      mainContent.appendChild(wrapper);
      warn(
        'Product grid not found; appended embed to "' +
          (mainContent.id || mainContent.tagName) +
          '".'
      );
    }
  }

  // ── Entry Point ────────────────────────────────────────────────────────────

  /**
   * localStorage key used to persist the configurator session ID per product.
   * Scoped to the Shopify page pathname so different products never collide.
   * e.g. 'vc:session:/products/my-48-inch-vanity'
   */
  function sessionStorageKey() {
    return 'vc:session:' + window.location.pathname;
  }

  /**
   * Builds the iframe URL from the metafield base URL.
   *   - Always appends readonly=1 (embedded mode restrictions).
   *   - Restores a previously-saved session ID (?id=UUID) so the user
   *     sees their previous design after a page reload.
   */
  function buildEmbedUrl(baseUrl) {
    try {
      var url = new URL(baseUrl);
      url.searchParams.set('readonly', '1');

      // Restore previous session for this product page (if any)
      var savedId = localStorage.getItem(sessionStorageKey());
      if (savedId) {
        url.searchParams.set('id', savedId);
        log('Restoring session: ' + savedId);
      }

      return url.toString();
    } catch (e) {
      // Fallback for environments where URL constructor is unavailable
      var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
      return baseUrl + sep + 'readonly=1';
    }
  }

  function init() {
    var visualizerUrl = getVisualizerUrl();

    if (!visualizerUrl) {
      log(
        'No visualizerUrl metafield detected — standard product page will render.'
      );
      return;
    }

    log('visualizerUrl detected: ' + visualizerUrl);

    var embedUrl = buildEmbedUrl(visualizerUrl);
    log('Embedding in read-only mode: ' + embedUrl);

    hideProductGrid();
    injectIframe(embedUrl);
  }

  /**
   * Single message listener that handles all postMessage events from the
   * embedded configurator iframe:
   *
   *  vanity:checkout — navigate window.top to Shopify cart (bypasses X-Frame-Options)
   *  vanity:session  — persist the configurator's instanceId in localStorage so
   *                    it can be restored as ?id=UUID on the next page load.
   */
  function listenForMessages() {
    window.addEventListener('message', function (event) {
      if (!event.data || typeof event.data.type !== 'string') return;

      // ── Checkout redirect ──────────────────────────────────────────────────
      if (event.data.type === 'vanity:checkout') {
        var cartUrl = event.data.cartUrl;
        if (typeof cartUrl !== 'string' || !cartUrl.startsWith('http')) {
          warn('Received vanity:checkout with invalid cartUrl: ' + cartUrl);
          return;
        }
        log('Navigating to Shopify cart: ' + cartUrl);
        window.top.location.href = cartUrl;
        return;
      }

      // ── Session persistence ────────────────────────────────────────────────
      // The configurator posts this once it has resolved its instanceId.
      // We store it keyed by the current page pathname so each product page
      // maintains its own session slot.
      if (event.data.type === 'vanity:session') {
        var instanceId = event.data.instanceId;
        if (typeof instanceId === 'string' && instanceId.length > 0) {
          var key = sessionStorageKey();
          localStorage.setItem(key, instanceId);
          log('Session persisted: ' + key + ' = ' + instanceId);
        }
        return;
      }
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      listenForMessages();
    });
  } else {
    init();
    listenForMessages();
  }
})();
